import type { PackageStorageResponse, StoragePlaceResponse } from '@/storageAreas/api/models/storageAreaResponse';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { PlaceAccessDirection, setAccessDirection } from 'shared/storageAreas/domain/placeAccessDirection';

export interface EditPackageStorage {
  storageAreaId: string;
  packageStorage: PackageStorageResponse;
}

export interface CopyPackageStorage {
  packageStorage: PackageStorageResponse;
}

export type StoragePlace = StoragePlaceResponse;

export interface GenerateGridOptions {
  columns: number;
  rows: number;
  horizontalGap: number;
  verticalGap: number;
}

export type GapAxis = 'horizontal' | 'vertical';

function clonePlaces(places: StoragePlace[]): StoragePlace[] {
  return places.map((p) => ({ ...p }));
}

export const useSelectedPackageStorageStore = defineStore('selectedPackageStorageStore', () => {
  const operation = ref<'create' | 'edit' | 'none'>('none');

  const model = ref<PackageStorageResponse>();

  const defaultDefaultPlaceLength = 0;
  const defaultPlaceLength = ref<number>(defaultDefaultPlaceLength);
  const defaultPlaceLengthInitial = computed(() => model.value?.defaultPlaceLength);
  const defaultPlaceLengthChanged = computed(
    () => operation.value === 'edit' && defaultPlaceLength.value !== defaultPlaceLengthInitial.value,
  );

  const defaultDefaultPlaceWidth = 0;
  const defaultPlaceWidth = ref<number>(defaultDefaultPlaceWidth);
  const defaultPlaceWidthInitial = computed(() => model.value?.defaultPlaceWidth);
  const defaultPlaceWidthChanged = computed(
    () => operation.value === 'edit' && defaultPlaceWidth.value !== defaultPlaceWidthInitial.value,
  );

  const defaultDefaultMaxStackCount = null;
  const defaultMaxStackCount = ref<number | null>(defaultDefaultMaxStackCount);
  const defaultMaxStackCountInitial = computed(() => model.value?.defaultMaxStackCount);
  const defaultMaxStackCountChanged = computed(
    () => operation.value === 'edit' && defaultMaxStackCount.value !== defaultMaxStackCountInitial.value,
  );

  const defaultDefaultMaxWeight = null;
  const defaultMaxWeight = ref<number | null>(defaultDefaultMaxWeight);
  const defaultMaxWeightInitial = computed(() => model.value?.defaultMaxWeight);
  const defaultMaxWeightChanged = computed(
    () => operation.value === 'edit' && defaultMaxWeight.value !== defaultMaxWeightInitial.value,
  );

  const defaultDefaultMaxHeight = null;
  const defaultMaxHeight = ref<number | null>(defaultDefaultMaxHeight);
  const defaultMaxHeightInitial = computed(() => model.value?.defaultMaxHeight);
  const defaultMaxHeightChanged = computed(
    () => operation.value === 'edit' && defaultMaxHeight.value !== defaultMaxHeightInitial.value,
  );

  const places = ref<StoragePlace[]>([]);
  const placesInitial = ref<string>('[]');
  const placesChanged = computed(
    () => operation.value === 'edit' && JSON.stringify(places.value) !== placesInitial.value,
  );

  // Grid configuration used both for generation and uniform re-spacing.
  const gridColumns = ref<number>(1);
  const gridRows = ref<number>(1);
  const horizontalGap = ref<number>(0);
  const verticalGap = ref<number>(0);

  // Bounds of the storage area in meters. Places are constrained to stay inside.
  // Infinity means the bounds are unknown (only the lower bound of 0 is enforced).
  const areaWidth = ref<number>(Number.POSITIVE_INFINITY);
  const areaLength = ref<number>(Number.POSITIVE_INFINITY);

  // The storage area's z-index. A place's z-index cannot be below this.
  const areaZIndex = ref<number>(0);

  const selectedPlaceIds = ref<string[]>([]);
  const selectedPlaces = computed(() => places.value.filter((p) => selectedPlaceIds.value.includes(p.id)));
  const editMultiplePlaces = computed(() => selectedPlaceIds.value.length > 1);

  // Undo history: snapshots of the places array captured before discrete edits.
  const maxHistory = 100;
  const undoStack = ref<string[]>([]);
  const redoStack = ref<string[]>([]);
  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);

  const changed = computed(
    () =>
      defaultPlaceLengthChanged.value ||
      defaultPlaceWidthChanged.value ||
      defaultMaxStackCountChanged.value ||
      defaultMaxWeightChanged.value ||
      defaultMaxHeightChanged.value ||
      placesChanged.value,
  );

  function create() {
    reset();
    operation.value = 'create';
  }

  function edit(action: EditPackageStorage) {
    reset();
    fill(action.packageStorage);
    model.value = action.packageStorage;
    operation.value = 'edit';
  }

  function copy(action: CopyPackageStorage) {
    reset();
    fill(action.packageStorage);
    places.value = places.value.map((p) => ({ ...p, id: crypto.randomUUID() }));
    placesInitial.value = JSON.stringify(places.value);
    operation.value = 'create';
  }

  function hide() {
    reset();
  }

  function reset() {
    operation.value = 'none';
    model.value = undefined;
    setDefaults();
  }

  function setDefaults() {
    defaultPlaceLength.value = defaultDefaultPlaceLength;
    defaultPlaceWidth.value = defaultDefaultPlaceWidth;
    defaultMaxStackCount.value = defaultDefaultMaxStackCount;
    defaultMaxWeight.value = defaultDefaultMaxWeight;
    defaultMaxHeight.value = defaultDefaultMaxHeight;
    places.value = [];
    placesInitial.value = '[]';
    selectedPlaceIds.value = [];
    gridColumns.value = 1;
    gridRows.value = 1;
    horizontalGap.value = 0;
    verticalGap.value = 0;
    areaWidth.value = Number.POSITIVE_INFINITY;
    areaLength.value = Number.POSITIVE_INFINITY;
    areaZIndex.value = 0;
    undoStack.value = [];
    redoStack.value = [];
  }

  function fill(packageStorage: PackageStorageResponse) {
    defaultPlaceLength.value = packageStorage.defaultPlaceLength;
    defaultPlaceWidth.value = packageStorage.defaultPlaceWidth;
    defaultMaxStackCount.value = packageStorage.defaultMaxStackCount;
    defaultMaxWeight.value = packageStorage.defaultMaxWeight;
    defaultMaxHeight.value = packageStorage.defaultMaxHeight;
    places.value = clonePlaces(packageStorage.places);
    placesInitial.value = JSON.stringify(places.value);
    selectedPlaceIds.value = [];
  }

  // --- Selection ---------------------------------------------------------

  function sameSelection(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((id) => set.has(id));
  }

  /** Applies a new selection, recording a restore point when it actually changes. */
  function applySelection(next: string[]) {
    if (sameSelection(next, selectedPlaceIds.value)) return;
    checkpoint();
    selectedPlaceIds.value = next;
  }

  function selectPlace(id: string, additive = false) {
    if (additive) {
      const next = selectedPlaceIds.value.includes(id)
        ? selectedPlaceIds.value.filter((x) => x !== id)
        : [...selectedPlaceIds.value, id];
      applySelection(next);
    } else {
      applySelection([id]);
    }
  }

  function setSelection(ids: string[]) {
    applySelection([...ids]);
  }

  function clearSelection() {
    applySelection([]);
  }

  // --- Editing -----------------------------------------------------------

  // --- Undo history ------------------------------------------------------

  /** Returns a serialized snapshot of the current places and selection. */
  function snapshot(): string {
    return JSON.stringify({ places: places.value, selection: selectedPlaceIds.value });
  }

  function pushOnto(stack: typeof undoStack, snap: string) {
    const current = stack.value;
    if (current.length > 0 && current[current.length - 1] === snap) return;
    stack.value = current.length >= maxHistory ? [...current.slice(1), snap] : [...current, snap];
  }

  /**
   * Pushes a snapshot onto the undo stack and starts a new history branch
   * (clears the redo stack). Use for a fresh edit's restore point.
   */
  function pushSnapshot(snap: string) {
    pushOnto(undoStack, snap);
    redoStack.value = [];
  }

  /** Captures the current places and selection as a restore point before an edit. */
  function checkpoint() {
    pushSnapshot(snapshot());
  }

  function applySnapshot(snap: string) {
    const parsed = JSON.parse(snap) as { places: StoragePlace[]; selection: string[] };
    places.value = parsed.places;
    const ids = new Set(parsed.places.map((p) => p.id));
    selectedPlaceIds.value = (parsed.selection ?? []).filter((id) => ids.has(id));
  }

  /** Restores the most recent snapshot from the undo stack. */
  function undo() {
    const stack = [...undoStack.value];
    const snap = stack.pop();
    if (snap === undefined) return;
    undoStack.value = stack;
    pushOnto(redoStack, snapshot());
    applySnapshot(snap);
  }

  /** Re-applies the most recently undone snapshot. */
  function redo() {
    const stack = [...redoStack.value];
    const snap = stack.pop();
    if (snap === undefined) return;
    redoStack.value = stack;
    pushOnto(undoStack, snapshot());
    applySnapshot(snap);
  }

  /** Sets the storage area bounds (meters) used to keep places inside the area. */
  function setAreaBounds(width: number, length: number) {
    areaWidth.value = width > 0 ? width : Number.POSITIVE_INFINITY;
    areaLength.value = length > 0 ? length : Number.POSITIVE_INFINITY;
    // Note: existing places are intentionally NOT re-clamped here. Setting the
    // bounds (e.g. when selecting a place) must not mutate places, otherwise it
    // would falsely mark the storage area as having unsaved changes. Clamping is
    // applied on the next explicit edit (move/resize/etc.).
  }

  /** Sets the storage area's z-index (the minimum allowed place z-index). */
  function setAreaZIndex(zIndex: number) {
    areaZIndex.value = Number.isFinite(zIndex) ? zIndex : 0;
  }

  /** Constrains a place's rectangle so it stays fully inside the storage area. */
  function clampPlace(place: StoragePlace): StoragePlace {
    const maxWidth = areaWidth.value;
    const maxLength = areaLength.value;

    const width = Math.min(place.width, maxWidth);
    const length = Math.min(place.length, maxLength);
    const x = Math.max(0, Math.min(place.x, maxWidth - width));
    const y = Math.max(0, Math.min(place.y, maxLength - length));
    const zIndex = Math.max(place.zIndex, areaZIndex.value);

    if (x === place.x && y === place.y && width === place.width && length === place.length && zIndex === place.zIndex) {
      return place;
    }
    return { ...place, x, y, width, length, zIndex };
  }

  /**
   * Re-clamps every place into the current area bounds. Used when the storage
   * area is resized so places can never remain outside the area. Records a
   * restore point and only mutates when a place actually needs adjusting.
   * Returns true when something changed.
   */
  function clampAllToBounds(): boolean {
    const willChange = places.value.some((p) => clampPlace(p) !== p);
    if (!willChange) return false;
    checkpoint();
    places.value = places.value.map((p) => clampPlace(p));
    return true;
  }

  function newPlaceSize() {
    return {
      length: defaultPlaceLength.value > 0 ? defaultPlaceLength.value : 1000,
      width: defaultPlaceWidth.value > 0 ? defaultPlaceWidth.value : 1000,
    };
  }

  function nextIndex(): number {
    return places.value.reduce((max, p) => Math.max(max, p.index), 0) + 1;
  }

  function createPlace(x: number, y: number): StoragePlace {
    const size = newPlaceSize();
    return {
      id: crypto.randomUUID(),
      index: nextIndex(),
      x,
      y,
      length: size.length,
      width: size.width,
      maxStackCount: null,
      maxWeight: null,
      maxHeight: null,
      disabled: false,
      accessDirections: PlaceAccessDirection.All,
      zIndex: areaZIndex.value,
      column: null,
      row: null,
      accessibilityScoreDecrementPerBlocker: -10,
      displayText: null,
    };
  }

  const overlapEpsilon = 0.001; // mm — touching edges are allowed, real overlap is not.

  function placesOverlap(a: StoragePlace, b: StoragePlace): boolean {
    return (
      a.zIndex === b.zIndex &&
      a.x < b.x + b.width - overlapEpsilon &&
      a.x + a.width > b.x + overlapEpsilon &&
      a.y < b.y + b.length - overlapEpsilon &&
      a.y + a.length > b.y + overlapEpsilon
    );
  }

  /**
   * True when any affected place overlaps another place it did not already
   * overlap. Pre-existing overlaps are tolerated (so overlapping legacy data can
   * still be dragged apart), but new overlaps are rejected.
   */
  function createsNewOverlap(next: StoragePlace[], affected: Set<string>): boolean {
    const currentById = new Map(places.value.map((p) => [p.id, p]));
    for (const a of next) {
      if (!affected.has(a.id)) continue;
      for (const b of next) {
        if (a.id === b.id || !placesOverlap(a, b)) continue;
        const ca = currentById.get(a.id);
        const cb = currentById.get(b.id);
        if (!ca || !cb || !placesOverlap(ca, cb)) return true;
      }
    }
    return false;
  }

  function isGeometryPatch(patch: Partial<StoragePlace>): boolean {
    return 'x' in patch || 'y' in patch || 'width' in patch || 'length' in patch || 'zIndex' in patch;
  }

  /** Finds a position where a new place of the given size does not overlap existing ones. */
  function findFreePosition(width: number, length: number): { x: number; y: number } {
    const existing = places.value;
    const stepX = width > 0 ? width : 1000;
    const stepY = length > 0 ? length : 1000;
    const maxX = Number.isFinite(areaWidth.value) ? areaWidth.value : 100000;
    const maxY = Number.isFinite(areaLength.value) ? areaLength.value : 100000;

    for (let y = 0; y + length <= maxY + overlapEpsilon; y += stepY) {
      for (let x = 0; x + width <= maxX + overlapEpsilon; x += stepX) {
        const candidate = { x, y, width, length } as StoragePlace;
        if (!existing.some((p) => placesOverlap(candidate, p))) return { x, y };
      }
    }

    // Fall back to the right of everything if the area is full.
    const maxRight = existing.reduce((m, p) => Math.max(m, p.x + p.width), 0);
    return { x: maxRight, y: 0 };
  }

  function addPlace(): StoragePlace {
    const size = newPlaceSize();
    const pos = findFreePosition(size.width, size.length);
    const place = clampPlace(createPlace(pos.x, pos.y));
    places.value = [...places.value, place];
    selectedPlaceIds.value = [place.id];
    return place;
  }

  function updatePlace(id: string, patch: Partial<StoragePlace>) {
    const next = places.value.map((p) => (p.id === id ? clampPlace({ ...p, ...patch }) : p));
    if (isGeometryPatch(patch) && createsNewOverlap(next, new Set([id]))) return;
    places.value = next;
  }

  function updatePlaces(ids: string[], patch: Partial<StoragePlace>) {
    const idSet = new Set(ids);
    const next = places.value.map((p) => (idSet.has(p.id) ? clampPlace({ ...p, ...patch }) : p));
    if (isGeometryPatch(patch) && createsNewOverlap(next, idSet)) return;
    places.value = next;
  }

  function removePlaces(ids: string[]) {
    const idSet = new Set(ids);
    places.value = places.value.filter((p) => !idSet.has(p.id));
    selectedPlaceIds.value = selectedPlaceIds.value.filter((id) => !idSet.has(id));
    reindex();
  }

  function moveBy(ids: string[], dx: number, dy: number) {
    const idSet = new Set(ids);
    const attempt = (ax: number, ay: number): StoragePlace[] | null => {
      const next = places.value.map((p) =>
        idSet.has(p.id) ? clampPlace({ ...p, x: p.x + ax, y: p.y + ay }) : p,
      );
      return createsNewOverlap(next, idSet) ? null : next;
    };
    // Allow sliding along a neighbour: try the full move, then each axis alone.
    const next = attempt(dx, dy) ?? attempt(dx, 0) ?? attempt(0, dy);
    if (next) places.value = next;
  }

  function resize(ids: string[], size: { length?: number; width?: number }) {
    const idSet = new Set(ids);
    const next = places.value.map((p) =>
      idSet.has(p.id)
        ? clampPlace({
            ...p,
            length: size.length !== undefined ? Math.max(0.001, size.length) : p.length,
            width: size.width !== undefined ? Math.max(0.001, size.width) : p.width,
          })
        : p,
    );
    if (createsNewOverlap(next, idSet)) return;
    places.value = next;
  }

  function setDirections(ids: string[], directions: number) {
    updatePlaces(ids, { accessDirections: directions });
  }

  function setDirection(ids: string[], direction: PlaceAccessDirection, enabled: boolean) {
    const idSet = new Set(ids);
    places.value = places.value.map((p) =>
      idSet.has(p.id) ? { ...p, accessDirections: setAccessDirection(p.accessDirections, direction, enabled) } : p,
    );
  }

  function setDisabled(ids: string[], disabled: boolean) {
    updatePlaces(ids, { disabled });
  }

  /** Sets the z-index of the given places (clamped to at least the area z-index). */
  function setZIndex(ids: string[], zIndex: number) {
    updatePlaces(ids, { zIndex: Math.max(zIndex, areaZIndex.value) });
  }

  /**
   * Shifts every place's z-index by delta. Used when the storage area's z-index
   * changes so places move with it while keeping their relative level.
   */
  function shiftPlacesZIndex(delta: number) {
    if (delta === 0 || places.value.length === 0) return;
    checkpoint();
    places.value = places.value.map((p) => ({ ...p, zIndex: p.zIndex + delta }));
  }

  function reindex() {
    places.value = places.value.map((p, i) => ({ ...p, index: i + 1 }));
  }

  // --- Grid generation & uniform gap ------------------------------------

  function generateGrid(options: GenerateGridOptions) {
    const columns = Math.max(1, Math.floor(options.columns));
    const rows = Math.max(1, Math.floor(options.rows));
    const hGap = Math.max(0, options.horizontalGap);
    const vGap = Math.max(0, options.verticalGap);
    const size = newPlaceSize();

    gridColumns.value = columns;
    gridRows.value = rows;
    horizontalGap.value = hGap;
    verticalGap.value = vGap;

    const generated: StoragePlace[] = [];
    let index = 1;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        generated.push({
          id: crypto.randomUUID(),
          index: index++,
          x: col * (size.width + hGap),
          y: row * (size.length + vGap),
          length: size.length,
          width: size.width,
          maxStackCount: null,
          maxWeight: null,
          maxHeight: null,
          disabled: false,
          accessDirections: PlaceAccessDirection.All,
          zIndex: areaZIndex.value,
          column: col + 1,
          row: row + 1,
          accessibilityScoreDecrementPerBlocker: -10,
          displayText: null,
        });
      }
    }

    const clamped = generated.map((p) => clampPlace(p));
    places.value = clamped;
    selectedPlaceIds.value = clamped.map((p) => p.id);
  }

  /**
   * Adds a uniform gap along a single axis (horizontal = between columns,
   * vertical = between rows) for the given places (or all when none specified).
   * Only the chosen axis is repositioned; the other coordinate is preserved,
   * so a horizontal gap does not disturb vertical alignment and vice versa.
   */
  function applyGap(axis: GapAxis, gap: number, ids?: string[]) {
    const value = Math.max(0, gap);
    if (axis === 'horizontal') horizontalGap.value = value;
    else verticalGap.value = value;

    const target = ids && ids.length > 0 ? places.value.filter((p) => ids.includes(p.id)) : [...places.value];
    if (target.length === 0) return;

    const ordered = [...target].sort((a, b) => a.index - b.index);
    const columns = Math.max(1, Math.min(gridColumns.value, ordered.length));

    // Uniform cell size = max size among the affected places.
    const cellWidth = Math.max(...ordered.map((p) => p.width));
    const cellLength = Math.max(...ordered.map((p) => p.length));

    // Anchor at the top-left of the affected places.
    const originX = Math.min(...ordered.map((p) => p.x));
    const originY = Math.min(...ordered.map((p) => p.y));

    const patched = new Map<string, Partial<Pick<StoragePlace, 'x' | 'y'>>>();
    ordered.forEach((p, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      if (axis === 'horizontal') {
        patched.set(p.id, { x: originX + col * (cellWidth + value) });
      } else {
        patched.set(p.id, { y: originY + row * (cellLength + value) });
      }
    });

    places.value = places.value.map((p) => {
      const pos = patched.get(p.id);
      return pos ? clampPlace({ ...p, ...pos }) : p;
    });
  }

  return {
    create,
    edit,
    copy,
    hide,
    operation,
    defaultPlaceLength,
    defaultPlaceLengthChanged,
    defaultPlaceWidth,
    defaultPlaceWidthChanged,
    defaultMaxStackCount,
    defaultMaxStackCountChanged,
    defaultMaxWeight,
    defaultMaxWeightChanged,
    defaultMaxHeight,
    defaultMaxHeightChanged,
    places,
    placesChanged,
    selectedPlaceIds,
    selectedPlaces,
    editMultiplePlaces,
    gridColumns,
    gridRows,
    horizontalGap,
    verticalGap,
    changed,
    canUndo,
    canRedo,
    // selection
    selectPlace,
    setSelection,
    clearSelection,
    // undo
    snapshot,
    pushSnapshot,
    checkpoint,
    undo,
    redo,
    // editing
    setAreaBounds,
    setAreaZIndex,
    areaZIndex,
    clampAllToBounds,
    addPlace,
    updatePlace,
    updatePlaces,
    removePlaces,
    moveBy,
    resize,
    setDirections,
    setDirection,
    setDisabled,
    setZIndex,
    shiftPlacesZIndex,
    reindex,
    // layout
    generateGrid,
    applyGap,
  };
});
